import React from 'react'
import { TestType, ProductTypes, Lab, CategoryType } from '../../types'
import {
  useUserForm,
  useSelectTestForm,
  useSymptomsForm,
  useTestSessionForm,
  usePreviousExposureForm,
  useCouponForm,
  useInsuranceForm,
} from '../../forms'
import { useCustomerRequestTestSession } from '../../hooks/customerRequestTestSession'
import { AppointmentBookingHours } from '../../constants/common'
import { InsuranceProps } from '../../constants/types'

export const INITIAL_APPLICANTS_COUNT = 1 // Represents the current user

export const useRequestTestSession = ({
  isNewUser,
  isWalkin,
}: {
  isNewUser: boolean
  isWalkin?: boolean
}) => {
  const [insuranceForm, setInsuranceForm] = React.useState<InsuranceProps>({
    isInsurance: false,
    insuranceId: null,
  })
  const insuranceFormNewUser = useInsuranceForm({ onSubmit: () => {} })
  const [requestTestSession, { loading: isRequestSessionLoading }] = useCustomerRequestTestSession()
  const couponForm = useCouponForm({ onSubmit: () => {} })
  const symptomsForm = useSymptomsForm({ onSubmit: () => {} })
  const previousExposureForm = usePreviousExposureForm({ onSubmit: () => {} })
  const userForm = useUserForm({
    validationSchema: isNewUser ? 'fullUser' : 'idOnly',
    onSubmit: () => {},
  })
  const selectTestForm = useSelectTestForm({
    onSubmit: () => {},
  })
  const testSessionForm = useTestSessionForm({
    minHoursBeforeBooking: isWalkin ? AppointmentBookingHours.WALK_IN : undefined,
    onSubmit: async (values, { setFieldValue }) => {
      const { testCount } = selectTestForm.values

      if (values.categoryType === CategoryType.Covid19) {
        if (symptomsForm.isValid) {
          try {
            const testSessionData = {
              isInsurance: insuranceForm.isInsurance ? true : false,
              date: values.date,
              testType: values.testType,
              category: CategoryType[values.categoryType],
              gmt: new Date().getTimezoneOffset(),
              Address: {
                city: values.Address.city || '',
                state: values.Address?.state || '',
                street: values.Address?.street || '',
                zip: values.Address?.zip || '',
                country: values.Address.country || '',
                county: values.Address.county || '',
                complement: values.Address.complement || '',
                lat: values.Address.lat || null,
                lon: values.Address.lon || null,
              },
              lab: Lab[values.lab as Lab] || Lab['DendiLax'],
              Members: [
                {
                  User: {
                    id: userForm.values.id,
                    email: userForm.values.email,
                    firstName: userForm.values.firstName,
                    lastName: userForm.values.lastName,
                    phoneNumber: userForm.values.phoneNumber,
                    birthDate: userForm.values.birthDate,
                    gender: userForm.values.gender,
                    ethnicity: userForm.values.ethnicity,
                    race: userForm.values.race,
                    optInMarketing: userForm.values.optInMarketing,
                    ...(userForm.values.Address?.country &&
                    userForm.values.Address.city &&
                    userForm.values.Address?.state &&
                    userForm.values.Address?.street &&
                    userForm.values.Address?.zip
                      ? {
                          Address: {
                            country: userForm.values.Address.country,
                            state: userForm.values.Address.state,
                            street: userForm.values.Address.street,
                            zip: userForm.values.Address.zip,
                            city: userForm.values.Address.city,
                          },
                        }
                      : {}),
                  },
                  isMain: true,
                  symptoms: symptomsForm.values.symptoms as string[],
                  alreadyTested: previousExposureForm.values.alreadyTested,
                  knownExposure: previousExposureForm.values.knownExposure,
                },
                ...(testCount > INITIAL_APPLICANTS_COUNT
                  ? values.Members.map(({ User, isMain, symptoms }) => ({
                      User: {
                        id: User.id,
                        email: User.email,
                        firstName: User.firstName,
                        lastName: User.lastName,
                        phoneNumber: User.phoneNumber,
                        birthDate: User.birthDate,
                        ...(User.Address?.city &&
                        User.Address.country &&
                        User.Address.state &&
                        User.Address?.street &&
                        User.Address?.zip
                          ? {
                              Address: {
                                city: User.Address.city,
                                country: User.Address.country,
                                state: User.Address.state,
                                street: User.Address.street,
                                zip: User.Address.zip,
                              },
                            }
                          : {}),
                      },
                      isMain,
                      symptoms,
                    }))
                  : []),
              ],
              testValues: values.TestValues,
            }

            const rapidMolecularTest = selectTestForm.values.tests
              .filter(({ type }) => type === ProductTypes.Rapid)
              .map(({ priceId, quantity }) => ({
                priceId,
                quantity,
              }))

            const otherTests = selectTestForm.values.tests
              .filter(({ type }) => type === ProductTypes.Covid19)
              .map(({ priceId, quantity }) => ({
                priceId,
                quantity,
              }))

            const antigenTest = selectTestForm.values.tests
              .filter(({ type }) => type === ProductTypes.Antigen)
              .map(({ priceId, quantity }) => ({
                priceId,
                quantity,
              }))

            const lucira = selectTestForm.values.tests
              .filter(({ type }) => type === ProductTypes.Lucira)
              .map(({ priceId, quantity }) => ({
                priceId,
                quantity,
              }))

            const uniqueTestType = selectTestForm.values.tests
              .map((item) => item.type)
              .filter((value, index, self) => self.indexOf(value) === index)
            let newTestSession
            let paymentSecret = []

            if (lucira.length) {
              const { data } = await requestTestSession({
                variables: {
                  inputData: {
                    ...testSessionData,
                    testType: TestType.Lucira,
                    category: CategoryType[values.categoryType],
                    Products: lucira,
                    coupon: couponForm.values.coupon,
                    totalTestTypes: uniqueTestType.length,
                    insuranceFormNewUser: insuranceFormNewUser.values,
                    isInsurance: insuranceForm.isInsurance,
                  },
                },
              })
              newTestSession = data
              paymentSecret.push(newTestSession?.customerRequestTestSession)
            }

            if (antigenTest.length) {
              const { data } = await requestTestSession({
                variables: {
                  inputData: {
                    ...testSessionData,
                    testType: TestType.IndicaidAntigen,
                    category: CategoryType[values.categoryType],
                    Products: antigenTest,
                    coupon: couponForm.values.coupon,
                    totalTestTypes: uniqueTestType.length,
                    insuranceFormNewUser: insuranceFormNewUser.values,
                    isInsurance: insuranceForm.isInsurance,
                  },
                },
              })
              newTestSession = data
              paymentSecret.push(newTestSession?.customerRequestTestSession)
            }

            if (rapidMolecularTest.length) {
              const { data } = await requestTestSession({
                variables: {
                  inputData: {
                    ...testSessionData,
                    testType: TestType.RapidCovid19,
                    category: CategoryType[values.categoryType],
                    Products: rapidMolecularTest,
                    coupon: couponForm.values.coupon,
                    totalTestTypes: uniqueTestType.length,
                    insuranceFormNewUser: insuranceFormNewUser.values,
                    isInsurance: insuranceForm.isInsurance,
                  },
                },
              })
              newTestSession = data
              paymentSecret.push(newTestSession?.customerRequestTestSession)
            }
            if (otherTests.length) {
              const { data } = await requestTestSession({
                variables: {
                  inputData: {
                    ...testSessionData,
                    testType: TestType.Covid19,
                    category: CategoryType[values.categoryType],
                    Products: otherTests,
                    coupon: couponForm.values.coupon,
                    totalTestTypes: uniqueTestType.length,
                    insuranceFormNewUser: insuranceFormNewUser.values,
                    isInsurance: insuranceForm.isInsurance,
                  },
                },
              })
              newTestSession = data
              paymentSecret.push(newTestSession?.customerRequestTestSession)
            }
            setFieldValue('paymentSecret', paymentSecret)
            if (paymentSecret.length === uniqueTestType.length) return true
            return false
          } catch {
            // ignore error
          }
        }

        return false
      }

      try {
        const testSessionData = {
          isInsurance: insuranceForm.isInsurance ? true : false,
          date: values.date,
          testType: TestType[values.testType],
          category: CategoryType[values.categoryType],
          gmt: new Date().getTimezoneOffset(),
          Address: {
            city: values.Address.city || '',
            state: values.Address?.state || '',
            street: values.Address?.street || '',
            zip: values.Address?.zip || '',
            country: values.Address.country || '',
            county: values.Address.county || '',
            complement: values.Address.complement || '',
            lat: values.Address.lat || null,
            lon: values.Address.lon || null,
          },
          lab: Lab[values.lab as Lab] || Lab['ModmdLab'],
          Members: [
            {
              User: {
                id: userForm.values.id,
                email: userForm.values.email,
                firstName: userForm.values.firstName,
                lastName: userForm.values.lastName,
                phoneNumber: userForm.values.phoneNumber,
                birthDate: userForm.values.birthDate,
                gender: userForm.values.gender,
                ethnicity: userForm.values.ethnicity,
                race: userForm.values.race,
                optInMarketing: userForm.values.optInMarketing,
                ...(userForm.values.Address?.country &&
                userForm.values.Address.city &&
                userForm.values.Address?.state &&
                userForm.values.Address?.street &&
                userForm.values.Address?.zip
                  ? {
                      Address: {
                        country: userForm.values.Address.country,
                        state: userForm.values.Address.state,
                        street: userForm.values.Address.street,
                        zip: userForm.values.Address.zip,
                        city: userForm.values.Address.city,
                      },
                    }
                  : {}),
              },
              isMain: true,
              symptoms: symptomsForm.values.symptoms as string[],
              alreadyTested: previousExposureForm.values.alreadyTested,
              knownExposure: previousExposureForm.values.knownExposure,
            },
            ...(testCount > INITIAL_APPLICANTS_COUNT
              ? values.Members.map(({ User, isMain, symptoms }) => ({
                  User: {
                    id: User.id,
                    email: User.email,
                    firstName: User.firstName,
                    lastName: User.lastName,
                    phoneNumber: User.phoneNumber,
                    birthDate: User.birthDate,
                    ...(User.Address?.city &&
                    User.Address.country &&
                    User.Address.state &&
                    User.Address?.street &&
                    User.Address?.zip
                      ? {
                          Address: {
                            city: User.Address.city,
                            country: User.Address.country,
                            state: User.Address.state,
                            street: User.Address.street,
                            zip: User.Address.zip,
                          },
                        }
                      : {}),
                  },
                  isMain,
                  symptoms,
                }))
              : []),
          ],
          testValues: values.TestValues,
        }

        const sofiaStrep = selectTestForm.values.tests
          .filter(({ type }) => type === ProductTypes.SofiaStrep)
          .map(({ priceId, quantity }) => ({
            priceId,
            quantity,
          }))

        const acceavaStrep = selectTestForm.values.tests
          .filter(({ type }) => type === ProductTypes.AcceavaStrep)
          .map(({ priceId, quantity }) => ({
            priceId,
            quantity,
          }))

        const sofiaInfluenza = selectTestForm.values.tests
          .filter(({ type }) => type === ProductTypes.SofiaInfluenza)
          .map(({ priceId, quantity }) => ({
            priceId,
            quantity,
          }))

        const biosignflu = selectTestForm.values.tests
          .filter(({ type }) => type === ProductTypes.BioSignFlu)
          .map(({ priceId, quantity }) => ({
            priceId,
            quantity,
          }))

        const sofiaRSV = selectTestForm.values.tests
          .filter(({ type }) => type === ProductTypes.SofiaRsv)
          .map(({ priceId, quantity }) => ({
            priceId,
            quantity,
          }))

        const uniqueTestType = selectTestForm.values.tests
          .map((item) => item.type)
          .filter((value, index, self) => self.indexOf(value) === index)

        let newTestSession
        let paymentSecret = []

        if (sofiaRSV.length) {
          const { data } = await requestTestSession({
            variables: {
              inputData: {
                ...testSessionData,
                testType: TestType.SofiaRsv,
                category: CategoryType[values.categoryType],
                Products: sofiaRSV,
                coupon: couponForm.values.coupon,
                totalTestTypes: uniqueTestType.length,
                insuranceFormNewUser: insuranceFormNewUser.values,
                isInsurance: insuranceForm.isInsurance,
              },
            },
          })

          newTestSession = data
          paymentSecret.push(newTestSession?.customerRequestTestSession)
        }

        if (sofiaStrep.length) {
          const { data } = await requestTestSession({
            variables: {
              inputData: {
                ...testSessionData,
                testType: TestType.SofiaStrep,
                category: CategoryType[values.categoryType],
                Products: sofiaStrep,
                coupon: couponForm.values.coupon,
                totalTestTypes: uniqueTestType.length,
                insuranceFormNewUser: insuranceFormNewUser.values,
                isInsurance: insuranceForm.isInsurance,
              },
            },
          })

          newTestSession = data
          paymentSecret.push(newTestSession?.customerRequestTestSession)
        }

        if (acceavaStrep.length) {
          const { data } = await requestTestSession({
            variables: {
              inputData: {
                ...testSessionData,
                testType: TestType.AcceavaStrep,
                category: CategoryType[values.categoryType],
                Products: acceavaStrep,
                coupon: couponForm.values.coupon,
                totalTestTypes: uniqueTestType.length,
                insuranceFormNewUser: insuranceFormNewUser.values,
                isInsurance: insuranceForm.isInsurance,
              },
            },
          })

          newTestSession = data
          paymentSecret.push(newTestSession?.customerRequestTestSession)
        }

        if (sofiaInfluenza.length) {
          const { data } = await requestTestSession({
            variables: {
              inputData: {
                ...testSessionData,
                testType: TestType.SofiaInfluenza,
                category: CategoryType[values.categoryType],
                Products: sofiaInfluenza,
                coupon: couponForm.values.coupon,
                totalTestTypes: uniqueTestType.length,
                insuranceFormNewUser: insuranceFormNewUser.values,
                isInsurance: insuranceForm.isInsurance,
              },
            },
          })

          newTestSession = data
          paymentSecret.push(newTestSession?.customerRequestTestSession)
        }

        if (biosignflu.length) {
          const { data } = await requestTestSession({
            variables: {
              inputData: {
                ...testSessionData,
                testType: TestType.BioSignFlu,
                category: CategoryType[values.categoryType],
                Products: biosignflu,
                coupon: couponForm.values.coupon,
                totalTestTypes: uniqueTestType.length,
                insuranceFormNewUser: insuranceFormNewUser.values,
                isInsurance: insuranceForm.isInsurance,
              },
            },
          })

          newTestSession = data
          paymentSecret.push(newTestSession?.customerRequestTestSession)
        }

        setFieldValue('paymentSecret', paymentSecret)
        if (paymentSecret.length === uniqueTestType.length) return true
        return false
      } catch (err) {
        console.log(err)
        return false
      }
    },
  })

  const data = React.useMemo(
    () => ({
      userForm,
      symptomsForm,
      testSessionForm,
      selectTestForm,
      previousExposureForm,
      isRequestSessionLoading,
      couponForm,
      insuranceForm,
      setInsuranceForm,
      insuranceFormNewUser,
    }),
    [
      userForm,
      symptomsForm,
      testSessionForm,
      selectTestForm,
      previousExposureForm,
      isRequestSessionLoading,
      couponForm,
      insuranceForm,
      setInsuranceForm,
      insuranceFormNewUser,
    ]
  )

  return data
}
